import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useChatContext } from "stream-chat-react";

import { useActiveCall } from "@/features/call/context/ActiveCallContext";

// Voice-call handlers for the active channel: start (1:1 vs group) and join.
export const useChatCalls = () => {
  const { t } = useTranslation();
  const { channel, client } = useChatContext();
  const {
    startCall: startActiveCall,
    startGroupCall,
    joinCall,
    showToast,
  } = useActiveCall();

  const handleCallClick = useCallback(() => {
    if (!channel || !client?.user) return;

    // If there's already an active call on this channel, join it instead
    const existingCallId = (channel.data as any)?.activeCallId;
    if (existingCallId) {
      joinCall(existingCallId, channel.id).catch((err) => {
        console.error("[ChatPage] Failed to join existing call:", err);
        // Call no longer exists — clean up stale activeCallId so banner
        // disappears and the user can start a new call.
        if (client) {
          const ch = client.channel("messaging", channel.id);
          ch.updatePartial({ unset: ["activeCallId"] } as any).catch(
            console.error,
          );
        }
        showToast(t("chat.join_call_failed"), "error");
      });
      return;
    }

    const members = Object.values(channel.state.members);
    const memberCount = members.length;
    const callId = uuidv4();

    if (memberCount <= 2) {
      // 1:1 call — call startActiveCall directly (same pattern as group calls)
      // so that getOrCreate({ ring: true }) fires immediately without the
      // navigate → CallPage → useEffect round-trip that delays the ring.
      const otherMember = members.find((m) => m.user_id !== client.user?.id);
      if (!otherMember?.user_id) {
        showToast(t("chat.recipient_not_found"), "error");
        return;
      }
      const target = otherMember.user;
      startActiveCall(
        callId,
        {
          userId: otherMember.user_id,
          name: target?.name || target?.id || "User",
          image: target?.image as string | undefined,
          company: (target as any)?.company_name || (target as any)?.company,
          batchNo: (target as any)?.batch_no,
          visaType: (target as any)?.visa_type_descr,
          userType: (target as any)?.user_type,
        },
        channel.id,
      ).catch((err: Error) => {
        console.error("[ChatPage] Failed to start 1:1 call:", err);
        showToast(
          t("chat.start_call_failed", {
            error: err.message || "Unknown error",
          }),
          "error",
        );
      });
    } else {
      // Group call — use startGroupCall so ALL members are passed to
      // getOrCreate({ ring: true, data: { members: [...] } }).
      // Previously this called startCall (1:1) with only the first peer,
      // so only 1 member received the ring notification.
      const otherMembers = members.filter((m) => m.user_id !== client.user?.id);
      if (otherMembers.length === 0) return;
      const groupName = (channel.data as any)?.name || "Group Call";
      const groupPeers = otherMembers.map((m) => ({
        userId: m.user_id || m.user?.id || "unknown",
        name: m.user?.name || m.user_id || "User",
        image: m.user?.image as string | undefined,
        company: (m.user as any)?.company_name || (m.user as any)?.company,
        batchNo: (m.user as any)?.batch_no,
        visaType: (m.user as any)?.visa_type_descr,
        userType: (m.user as any)?.user_type,
      }));
      // Override the first peer's name with the group name for display
      groupPeers[0] = { ...groupPeers[0], name: groupName };
      startGroupCall(callId, groupPeers, channel.id);
    }
  }, [channel, client, startActiveCall, startGroupCall, joinCall, showToast, t]);

  // Handle joining an active group call from the banner
  const handleJoinCall = useCallback(
    (callId: string) => {
      if (!channel?.id) return;
      joinCall(callId, channel.id).catch((err) => {
        console.error("[ChatPage] Failed to join call:", err);
        // Call might no longer exist — clean up stale activeCallId
        if (client) {
          const ch = client.channel("messaging", channel.id);
          ch.updatePartial({ unset: ["activeCallId"] } as any).catch(
            console.error,
          );
        }
        showToast(t("chat.join_call_failed"), "error");
      });
    },
    [channel?.id, joinCall, client, showToast, t],
  );

  return { handleCallClick, handleJoinCall };
};
